// CohortNewAssortmentRuleCreationContainer.js
import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import debounce from 'lodash/debounce';
import brandicon from "../../../../assests/brandicon.svg";
import mvidicon from "../../../../assests/mvidicon.svg";
import offerpriceicon from "../../../../assests/offerpriceicon.svg";
import producticon from "../../../../assests/producticon.svg";
import varianticon from "../../../../assests/varianticon.svg";
import CohortNewRuleCreationComponent from "./component";
import { format } from 'date-fns';
import { useNavigate, useParams } from "react-router-dom";

const CohortNewAssortmentRuleCreationContainer = (props) => {
  const navigate = useNavigate();
  const {reference_id} = useParams()
  const [formData, setFormData] = useState({
    startDate: new Date(),
    ruleType: "",
    names: [],
    slotCount: 1,
    validationType: "RESTRICTED",
    maxPrice: ""
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [errors, setErrors] = useState({
    ruleType: false,
    names: false,
    maxPrice: false,
    validationType: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newRuleId, setNewRuleId] = useState(null);
  const [newCohortName, setNewCohortName] = useState(null);
  const [ruleFailureData, setRuleFailureData] = useState([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState('');
  const [showRuleFailurePopup, setShowRuleFailurePopup] = useState(false);
  const [filteredMvList, setFilteredMvList] = useState([]);
  const [showMvIdDropdown, setShowMvIdDropdown] = useState(false);
  const [ruleFailures, setRuleFailures] = useState([]);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showErrorMsg, setShowErrorMsg] = useState([])

  const validateForm = () => {
    const newErrors = {
      ruleType: !formData.ruleType,
      names: formData.ruleType !== "OfferPrice" && formData.names.length === 0,
      maxPrice: formData.ruleType === "OfferPrice" && !formData.maxPrice,
      validationType: formData.ruleType !== "OfferPrice" && !formData.validationType
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const validateInput = (value) => {
    return /^[a-zA-Z0-9\s]*$/.test(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "maxPrice") {
      if (/^[0-9]*\.?[0-9]*$/.test(value) || value === "") {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      return;
    }
    
    if (validateInput(value) || value === "") {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }

    if (name === "ruleType") {
      setFormData(prev => ({
        ...prev,
        names: [],
        maxPrice: "",
        validationType: "RESTRICTED",
        slotCount: 1
      }));
      setSearchInput("");
      setShowDropdown(false);
      setShowMvIdDropdown(false);
      setSearchResults([]);
      setFilteredMvList([]);
    }
  };

  // Search for Brand, Product, Variant
  const searchEntity = debounce((value) => {
    if (value.length > 0 && formData.ruleType && ["Brand", "Product", "Variant"].includes(formData.ruleType)) {
      setShowDropdown(true);
      setSearchLoading(true);

      let searchParam = '';
      let entityType = '';

      switch (formData.ruleType) {
        case "Brand":
          searchParam = value;
          entityType = 'brand';
          break;
        case "Product":
          searchParam = value;
          entityType = 'product';
          break;
        case "Variant":
          searchParam = value;
          entityType = 'variant';
          break;
        default:
          break;
      }

      const apiUrl = `/partner/api/v1/assortment/item/search?search=${encodeURIComponent(searchParam)}&searchEntityName=${entityType}`;

      props.getDataFromAPI(apiUrl, "GET", undefined, (response) => {
        const data = response || {};
        let results = [];

        if (Array.isArray(data)) {
          results = data.map(item => ({
            id: item.id,
            name: item.name
          }));
        }

        setSearchResults(results);
        setSearchLoading(false);
      }, (err) => {
        console.error("Search error:", err);
        setSearchResults([]);
        setSearchLoading(false);
      });
    } else {
      setShowDropdown(false);
      setSearchResults([]);
    }
  }, 300);

  // Search for MVID
  const searchMvId = debounce((value) => {
    if (value.length > 0 && formData.ruleType === "MVID") {
      setShowMvIdDropdown(true);
      setSearchLoading(true);

      props.getDataFromAPI(
        `/dashboard/api/v2/admin/mvs/all?limit=50&offset=0&isActive=1&search=${encodeURIComponent(value)}`,
        "GET",
        undefined,
        (response) => {
          const results = response?.list?.map(item => ({
            id: item.id,
            name: item.variant_name,
            displayName: `${item.id} - ${item.variant_name || 'N/A'}`
          })) || [];
          
          setFilteredMvList(results);
          setSearchLoading(false);
        },
        (err) => {
          console.error("MVID search error:", err);
          setFilteredMvList([]);
          setSearchLoading(false);
        }
      );
    } else {
      setShowMvIdDropdown(false);
      setFilteredMvList([]);
    }
  }, 300);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    if (validateInput(value) || value === "") {
      setSearchInput(value);
      
      if (formData.ruleType === "MVID") {
        searchMvId(value);
      } else if (["Brand", "Product", "Variant"].includes(formData.ruleType)) {
        searchEntity(value);
      }
    }
  };

  const handleAddName = (item) => {
    if (!formData.names.some(existingItem => existingItem.id === item.id)) {
      setFormData(prev => ({
        ...prev,
        names: [...prev.names, { id: item.id, name: item.name }]
      }));
      setErrors(prev => ({ ...prev, names: false }));
    }
    setSearchInput("");
    setShowDropdown(false);
    setShowMvIdDropdown(false);
  };

  const handleRemoveName = (idToRemove) => {
    setFormData(prev => ({
      ...prev,
      names: prev.names.filter(item => item.id !== idToRemove)
    }));
  };

  const handleSlotCountChange = (delta) => {
    setFormData(prev => ({
      ...prev,
      slotCount: Math.max(1, prev.slotCount + delta)
    }));
  };

  const handleDateChange = (newValue) => {
    setFormData(prev => ({ ...prev, startDate: newValue }));
  };

  // Helper function to parse rule failures
  const parseRuleFailures = (failureString, ruleTypeIds) => {
    const failures = [];
    
    if (failureString === "mv_not_present") {
      failures.push({
        failureType: "MANUFACTURER_VARIANT_NOT_PRESENT",
        failedItems: {
          ids: ruleTypeIds || [],
          message: "The manufacturer variants are not present in the system"
        }
      });
    } else if (failureString === "brand_not_present") {
      failures.push({
        failureType: "BRAND_NOT_PRESENT",
        failedItems: {
          ids: ruleTypeIds || [],
          message: "The brands are not present in the system"
        }
      });
    } else if (failureString === "product_not_present") {
      failures.push({
        failureType: "PRODUCT_NOT_PRESENT",
        failedItems: {
          ids: ruleTypeIds || [],
          message: "The products are not present in the system"
        }
      });
    } else if (failureString === "variant_not_present") {
      failures.push({
        failureType: "VARIANT_NOT_PRESENT",
        failedItems: {
          ids: ruleTypeIds || [],
          message: "The variants are not present in the system"
        }
      });
    } else {
      // Generic failure for unknown types
      failures.push({
        failureType: "UNKNOWN_FAILURE",
        failedItems: {
          ids: ruleTypeIds || [],
          message: failureString || "Unknown error occurred"
        }
      });
    }
    
    return failures;
  };

const handleCreateRule = async () => {
  if (!validateForm()) return;

  setIsSubmitting(true);

  const ruleTypeMap = {
    "Brand": "BRAND",
    "Variant": "VARIANT",
    "Product": "PRODUCT",
    "MVID": "MANUFACTURER_VARIANT",
    "OfferPrice": "OFFER_PRICE"
  };

  let payload = {
    type: "COHORT",
    reference_id: reference_id,
    start_date: format(formData.startDate, "yyyy-MM-dd"),
    rule_type: ruleTypeMap[formData.ruleType],
    condition: formData.validationType,
    value: formData.slotCount,
    max_price:
      formData.ruleType === "OfferPrice"
        ? parseFloat(formData.maxPrice)
        : null,
    rule_type_id:
      formData.ruleType === "OfferPrice"
        ? []
        : formData.names.map((item) => item.id),
  };

  props.getDataFromAPI(
    `/partner/api/v1/assortment/rule/create`,
    "POST",
    payload,
    (response) => {
      setIsSubmitting(false);

      const failures = response.rule_failures;

      if (failures) {
        // ✅ Failure message error me show karo
        toastr.warning("", failures);

        // ✅ Lekin success popup bhi dikhao
        setNewRuleId(response.id);
        setShowSuccessModal(true);
        setNewCohortName(response.reference_name);

      } else {
        // ✅ Normal success case
        toastr.success("Rule created successfully!");
        setNewRuleId(response.id);
        setShowSuccessModal(true);
        setNewCohortName(response.reference_name);
      }
    },
    (err) => {
      setIsSubmitting(false);
      toastr.error("Error", err.message || "Failed to create assortment");
    }
  );
};

  return (
    <CohortNewRuleCreationComponent
      formData={formData}
      searchInput={searchInput}
      searchResults={formData.ruleType === "MVID" ? filteredMvList : searchResults}
      searchLoading={searchLoading}
      showDropdown={formData.ruleType === "MVID" ? showMvIdDropdown : showDropdown}
      errors={errors}
      isSubmitting={isSubmitting}
      ruleTypes={[
        { value: "Brand", label: "Brand", icon: brandicon },
        { value: "Variant", label: "Variant", icon: varianticon },
        { value: "Product", label: "Product", icon: producticon },
        { value: "MVID", label: "MVID", icon: mvidicon },
        { value: "OfferPrice", label: "Offer Price", icon: offerpriceicon }
      ]}
      onChange={handleChange}
      onSearchInputChange={handleSearchInputChange}
      onAddName={handleAddName}
      onRemoveName={handleRemoveName}
      onSlotCountChange={handleSlotCountChange}
      onSubmit={handleCreateRule}
      onDateChange={handleDateChange}
      showSuccessModal={showSuccessModal}
      newRuleId={newRuleId}
      setShowSuccessModal={setShowSuccessModal}
      newCohortName={newCohortName}
      showRuleFailurePopup={showRuleFailurePopup}
      setShowRuleFailurePopup={setShowRuleFailurePopup}
      ruleFailureData={ruleFailureData}
      lastUpdatedTime={lastUpdatedTime}
      isMvidSearch={formData.ruleType === "MVID"}
      ruleFailures={ruleFailures}
      showFailureModal={showFailureModal}
      setShowFailureModal={setShowFailureModal}
      showErrorMsg={showErrorMsg}
    />
  );
};

function mapStateToProps(props) {
  return { props };
}

export default connect(mapStateToProps, { getDataFromAPI })(CohortNewAssortmentRuleCreationContainer);